import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../context/AuthContext";
import { fetchUserRedemptions, fetchUserAnalytics } from "../state/slices/userSlice";
import { fetchBookmarks, removeBookmark } from "../state/slices/bookmarkSlice";

const computeBadges = (a) => {
    const list = [];
    if (a.total_redeemed >= 1)
        list.push({ icon: "ph-ticket", label: "First Saver", desc: "Redeemed your first coupon", color: "#4CAF50" });
    if (a.total_saved >= 25)
        list.push({ icon: "ph-piggy-bank", label: "Budget Hero", desc: "Saved $25 or more", color: "#2196F3" });
    if (a.total_saved >= 100)
        list.push({ icon: "ph-trophy", label: "Super Saver", desc: "Saved $100 or more", color: "#FF9800" });
    if (a.total_saved >= 500)
        list.push({ icon: "ph-crown", label: "Savings Legend", desc: "Saved $500 or more", color: "#9C27B0" });
    if (a.days_as_member >= 30)
        list.push({ icon: "ph-calendar-check", label: "Loyal Member", desc: "Member for 30+ days", color: "#00BCD4" });
    if (a.days_as_member >= 365)
        list.push({ icon: "ph-star", label: "Year-Round Saver", desc: "Member for 1+ year", color: "#FFD700" });
    if (a.unique_businesses >= 3)
        list.push({ icon: "ph-buildings", label: "Explorer", desc: "Visited 3+ businesses", color: "#FF5722" });
    if (a.total_redeemed >= 10)
        list.push({ icon: "ph-lightning", label: "Power User", desc: "10+ coupons redeemed", color: "#E91E63" });
    if ((a.categories?.length || 0) >= 3)
        list.push({ icon: "ph-grid-four", label: "Category King", desc: "Explored 3+ categories", color: "#3F51B5" });
    if (a.active_weeks >= 4)
        list.push({ icon: "ph-fire", label: "Streak Master", desc: "Active 4+ weeks", color: "#FF6B35" });
    if (a.ratings_given >= 5)
        list.push({ icon: "ph-chat-circle-dots", label: "Community Voice", desc: "Left 5+ ratings", color: "#26A69A" });
    if ((a.cities?.length || 0) >= 2)
        list.push({ icon: "ph-map-trifold", label: "Globetrotter", desc: "Explored 2+ cities", color: "#8BC34A" });
    return list;
};

const LOCKED_BADGE_LABELS = ["First Saver", "Budget Hero", "Super Saver", "Explorer", "Power User", "Streak Master"];

const SkeletonBar = ({ width = "100%", height = 12 }) => (
    <div
        className="rounded-4"
        style={{ width, height, background: "#f0f0f0", animation: "pulse 1.5s ease-in-out infinite" }}
    />
);

const cardStyle = {
    border: "1px solid #ebebeb",
    borderRadius: 16,
    background: "#fff",
    boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
};

const UserDashboard = () => {
    const { user, isSubscribed, token } = useAuth();
    const dispatch = useDispatch();
    const redemptions = useSelector((s) => s.user.redemptions);
    const redemptionsStatus = useSelector((s) => s.user.redemptionsStatus);
    const analytics = useSelector((s) => s.user.analytics);
    const analyticsStatus = useSelector((s) => s.user.analyticsStatus);
    const bookmarks = useSelector((s) => s.bookmarks.bookmarks);
    const bookmarksStatus = useSelector((s) => s.bookmarks.status);

    useEffect(() => {
        if (!user?.id) return;
        dispatch(fetchUserRedemptions({ userId: user.id, token }));
        dispatch(fetchUserAnalytics({ userId: user.id, token }));
        dispatch(fetchBookmarks(token));
    }, [user, dispatch]);

    const redemptionsLoading = redemptionsStatus === "idle" || redemptionsStatus === "loading";
    const analyticsLoading = analyticsStatus === "idle" || analyticsStatus === "loading";

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        return new Date(dateStr).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const badges = analytics ? computeBadges(analytics) : [];
    const maxMonthlySavings = analytics?.monthly_savings?.length
        ? Math.max(...analytics.monthly_savings.map((m) => m.savings), 1)
        : 1;
    const maxCategoryCount = analytics?.categories?.length
        ? Math.max(...analytics.categories.map((c) => c.count))
        : 1;

    const bestMonth = analytics?.monthly_savings?.reduce(
        (best, m) => (m.savings > (best?.savings || 0) ? m : best),
        null
    );

    const kpis = [
        {
            value: analytics ? `$${analytics.total_saved.toFixed(2)}` : "—",
            label: "Total Saved",
            icon: "ph-piggy-bank",
            accent: "#2575fc",
            bg: "#eef4ff",
        },
        {
            value: analytics ? analytics.total_redeemed : "—",
            label: "Coupons Redeemed",
            icon: "ph-ticket",
            accent: "#10b981",
            bg: "#ecfdf5",
        },
        {
            value: analytics ? `$${analytics.avg_savings.toFixed(2)}` : "—",
            label: "Avg. Savings / Coupon",
            icon: "ph-chart-line-up",
            accent: "#f59e0b",
            bg: "#fffbeb",
        },
        {
            value: analytics ? analytics.unique_businesses : "—",
            label: "Businesses Visited",
            icon: "ph-buildings",
            accent: "#8b5cf6",
            bg: "#f5f3ff",
        },
    ];

    const categoryColors = ["#2575fc", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4"];

    return (
        <section className="py-80" style={{ background: "#f7f8fc", minHeight: "100vh" }}>
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
            <div className="container container-lg">

                {/* ── Hero Header ── */}
                <div
                    className="p-40 mb-40 d-flex align-items-center justify-content-between flex-wrap gap-24"
                    style={{
                        background: "linear-gradient(135deg, #1a3a6b 0%, #2575fc 100%)",
                        borderRadius: 20,
                        boxShadow: "0 8px 40px rgba(37,117,252,0.22)",
                    }}
                >
                    <div>
                        <p
                            className="mb-8"
                            style={{
                                fontSize: 11,
                                letterSpacing: 2,
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.6)",
                                fontWeight: 600,
                            }}
                        >
                            Member Dashboard
                        </p>
                        <h2
                            className="mb-8 fw-bold"
                            style={{ color: "#fff", fontSize: 30, lineHeight: 1.2 }}
                        >
                            Welcome back, {user?.first_name}!
                        </h2>
                        <p className="mb-0" style={{ fontSize: 14, color: "rgba(255,255,255,0.65)" }}>
                            {analytics
                                ? `Member since ${analytics.member_since} · ${analytics.days_as_member} days of savings`
                                : "Your savings journey is tracked here."}
                        </p>
                    </div>
                    <div className="text-center">
                        <div
                            className="d-inline-flex align-items-center gap-10 px-20 py-10 mb-8"
                            style={{
                                background: isSubscribed ? "rgba(72,199,116,0.18)" : "rgba(255,255,255,0.12)",
                                border: `1px solid ${isSubscribed ? "rgba(72,199,116,0.45)" : "rgba(255,255,255,0.25)"}`,
                                borderRadius: 99,
                            }}
                        >
                            <span
                                style={{
                                    width: 9,
                                    height: 9,
                                    borderRadius: "50%",
                                    background: isSubscribed ? "#48c774" : "rgba(255,255,255,0.45)",
                                    boxShadow: isSubscribed ? "0 0 0 3px rgba(72,199,116,0.25)" : "none",
                                    display: "inline-block",
                                    flexShrink: 0,
                                }}
                            />
                            <span style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
                                {isSubscribed ? "Fun Card Active" : "Not Subscribed"}
                            </span>
                        </div>
                        {isSubscribed && analytics?.subscription_end && (
                            <p
                                className="mb-0"
                                style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", textAlign: "center" }}
                            >
                                Renews {analytics.subscription_end}
                            </p>
                        )}
                        {!isSubscribed && (
                            <div>
                                <Link
                                    to="/cart"
                                    className="d-inline-block px-20 py-8 fw-semibold"
                                    style={{
                                        background: "#fff",
                                        color: "#2575fc",
                                        borderRadius: 99,
                                        fontSize: 13,
                                        textDecoration: "none",
                                        marginTop: 8,
                                    }}
                                >
                                    Purchase Fun Card
                                </Link>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── KPI Cards ── */}
                <div className="row gy-4 mb-32">
                    {kpis.map((kpi, i) => (
                        <div className="col-6 col-lg-3" key={i}>
                            <div className="p-28 rounded-16 h-100 d-flex flex-column" style={cardStyle}>
                                <div
                                    className="d-flex align-items-center justify-content-center rounded-12 mb-20"
                                    style={{ width: 48, height: 48, background: kpi.bg, flexShrink: 0 }}
                                >
                                    <i className={`ph ${kpi.icon}`} style={{ fontSize: 22, color: kpi.accent }} />
                                </div>
                                {analyticsLoading ? (
                                    <SkeletonBar width="55%" height={28} />
                                ) : (
                                    <div
                                        className="fw-bold mb-4"
                                        style={{ fontSize: 26, color: kpi.accent, lineHeight: 1 }}
                                    >
                                        {kpi.value}
                                    </div>
                                )}
                                <p className="mb-0 fw-medium" style={{ fontSize: 13, color: "#999", marginTop: 6 }}>
                                    {kpi.label}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Bookmarked Coupons ── */}
                <div className="p-32 rounded-16 mb-24" style={cardStyle}>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-12 mb-24">
                        <div>
                            <h6 className="fw-bold mb-4" style={{ fontSize: 16 }}>Bookmarked Coupons</h6>
                            <p className="mb-0" style={{ fontSize: 13, color: "#aaa" }}>
                                {bookmarks.length > 0 ? `${bookmarks.length} saved coupon${bookmarks.length !== 1 ? "s" : ""}` : "Coupons you've saved for later"}
                            </p>
                        </div>
                        {bookmarks.length > 0 && (
                            <Link to="/shop" className="px-16 py-8 rounded-pill fw-semibold" style={{ background: "#eef4ff", color: "#2575fc", fontSize: 13, textDecoration: "none" }}>
                                Browse More
                            </Link>
                        )}
                    </div>
                    {bookmarksStatus === "loading" ? (
                        <div className="d-flex flex-column gap-12">
                            {[1,2,3].map((i) => (
                                <div key={i} className="rounded-12" style={{ height: 72, background: "#f5f5f5", animation: "pulse 1.5s ease-in-out infinite" }} />
                            ))}
                        </div>
                    ) : bookmarks.length > 0 ? (
                        <div className="d-flex flex-column gap-10">
                            {bookmarks.map((coupon) => (
                                <div key={coupon.id} className="d-flex align-items-center gap-16 p-16 rounded-12" style={{ border: "1px solid #f0f0f0", background: "#fafafa", transition: "box-shadow 0.15s" }}>
                                    {/* Image */}
                                    <div className="flex-shrink-0 rounded-10 overflow-hidden" style={{ width: 56, height: 56, background: "#f3f4f6", position: "relative" }}>
                                        {coupon.image_url ? (
                                            <img src={coupon.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center h-100">
                                                <i className="ph ph-tag" style={{ fontSize: 22, color: "#d1d5db" }} />
                                            </div>
                                        )}
                                    </div>
                                    {/* Info */}
                                    <div style={{ flex: 1, minWidth: 0, paddingLeft: 8 }}>
                                        <div className="d-flex align-items-center gap-8 mb-4">
                                            <span className="px-8 py-2 rounded-pill fw-semibold" style={{ fontSize: 10, background: "#f5f3ff", color: "#7c3aed", whiteSpace: "nowrap" }}>
                                                {coupon.discount_category}
                                            </span>
                                            {coupon.business_name && (
                                                <span style={{ fontSize: 11, color: "#aaa", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{coupon.business_name}</span>
                                            )}
                                        </div>
                                        <Link to={`/coupon/${coupon.id}`} style={{ textDecoration: "none" }}>
                                            <p className="fw-semibold mb-0" style={{ fontSize: 14, color: "#1a1a1a", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{coupon.title}</p>
                                        </Link>
                                    </div>
                                    {/* Savings */}
                                    <div className="flex-shrink-0 text-end" style={{ minWidth: 80, paddingLeft: 8 }}>
                                        <p className="fw-bold mb-0" style={{ fontSize: 15, color: "#10b981" }}>${coupon.dollar_savings_amount}</p>
                                        <p className="mb-0" style={{ fontSize: 11, color: "#aaa" }}>savings</p>
                                    </div>
                                    {/* Actions */}
                                    <div className="d-flex align-items-center gap-8 flex-shrink-0" style={{ paddingLeft: 8 }}>
                                        <Link to={`/coupon/${coupon.id}`} className="px-14 py-8 rounded-8 fw-semibold" style={{ fontSize: 12, background: "#2575fc", color: "#fff", textDecoration: "none", whiteSpace: "nowrap" }}>
                                            View
                                        </Link>
                                        <button
                                            type="button"
                                            onClick={() => dispatch(removeBookmark({ couponId: coupon.id, token }))}
                                            style={{ background: "none", border: "1px solid #fee2e2", borderRadius: 8, padding: "7px 10px", cursor: "pointer", lineHeight: 1, color: "#ef4444" }}
                                            title="Remove bookmark"
                                        >
                                            <i className="ph ph-trash" style={{ fontSize: 14 }} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="d-flex flex-column align-items-center justify-content-center py-40 text-center">
                            <i className="ph ph-bookmark-simple" style={{ fontSize: 40, color: "#e0e0e0" }} />
                            <p className="mb-0 mt-12" style={{ fontSize: 14, color: "#bbb" }}>No bookmarks yet</p>
                            <Link to="/shop" className="mt-12 px-20 py-8 rounded-pill fw-semibold" style={{ background: "#2575fc", color: "#fff", fontSize: 13, textDecoration: "none" }}>
                                Browse Coupons
                            </Link>
                        </div>
                    )}
                </div>

                {/* ── Monthly Savings Chart + Category Breakdown ── */}
                <div className="row gy-4 mb-32">
                    {/* Monthly Bar Chart */}
                    <div className="col-lg-7">
                        <div className="p-32 rounded-16 h-100" style={cardStyle}>
                            <h6 className="fw-bold mb-4" style={{ fontSize: 16 }}>Monthly Savings</h6>
                            <p style={{ fontSize: 13, color: "#aaa", marginBottom: 32 }}>
                                Your savings over the last 6 months
                            </p>

                            {analyticsLoading ? (
                                <div
                                    className="d-flex gap-12 align-items-end"
                                    style={{ height: 160, paddingBottom: 24 }}
                                >
                                    {[40, 70, 55, 90, 65, 80].map((h, i) => (
                                        <div
                                            key={i}
                                            className="flex-1 rounded-top-4"
                                            style={{
                                                height: `${h}%`,
                                                background: "#f0f0f0",
                                                animation: "pulse 1.5s ease-in-out infinite",
                                                animationDelay: `${i * 0.1}s`,
                                                borderRadius: "4px 4px 0 0",
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="d-flex gap-8 align-items-end" style={{ height: 160 }}>
                                    {(analytics?.monthly_savings || []).map((m, i) => {
                                        const pct = (m.savings / maxMonthlySavings) * 100;
                                        const isEmpty = m.savings === 0;
                                        return (
                                            <div
                                                key={i}
                                                className="d-flex flex-column align-items-center flex-1"
                                                style={{ height: "100%" }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: 10,
                                                        color: "#bbb",
                                                        fontWeight: 600,
                                                        marginBottom: 4,
                                                        height: 16,
                                                        display: "flex",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    {m.savings > 0 ? `$${m.savings.toFixed(0)}` : ""}
                                                </span>
                                                <div
                                                    style={{
                                                        flex: 1,
                                                        width: "100%",
                                                        display: "flex",
                                                        alignItems: "flex-end",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: "100%",
                                                            height: isEmpty ? 4 : `${Math.max(pct, 4)}%`,
                                                            background: isEmpty
                                                                ? "#f0f0f0"
                                                                : "linear-gradient(180deg, #4d94ff 0%, #1a3a6b 100%)",
                                                            borderRadius: "4px 4px 0 0",
                                                            transition: "height 0.8s ease",
                                                        }}
                                                    />
                                                </div>
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        color: "#aaa",
                                                        fontWeight: 500,
                                                        marginTop: 8,
                                                    }}
                                                >
                                                    {m.month}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {analytics && (
                                <div
                                    className="d-flex gap-24 mt-24 pt-20"
                                    style={{ borderTop: "1px solid #f5f5f5" }}
                                >
                                    <div>
                                        <p className="mb-0" style={{ fontSize: 11, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                            Best Month
                                        </p>
                                        <p className="mb-0 fw-bold" style={{ fontSize: 15 }}>
                                            {bestMonth?.month || "—"}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="mb-0" style={{ fontSize: 11, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                            Active Weeks
                                        </p>
                                        <p className="mb-0 fw-bold" style={{ fontSize: 15 }}>
                                            {analytics.active_weeks}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="mb-0" style={{ fontSize: 11, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                            Avg Rating Given
                                        </p>
                                        <p className="mb-0 fw-bold" style={{ fontSize: 15 }}>
                                            {analytics.avg_rating_given ? `${analytics.avg_rating_given} ★` : "—"}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="mb-0" style={{ fontSize: 11, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                            Reviews Left
                                        </p>
                                        <p className="mb-0 fw-bold" style={{ fontSize: 15 }}>
                                            {analytics.ratings_given}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Category Breakdown */}
                    <div className="col-lg-5">
                        <div className="p-32 rounded-16 h-100" style={cardStyle}>
                            <h6 className="fw-bold mb-4" style={{ fontSize: 16 }}>Top Categories</h6>
                            <p style={{ fontSize: 13, color: "#aaa", marginBottom: 28 }}>
                                Where you save the most
                            </p>
                            {analyticsLoading ? (
                                <div className="d-flex flex-column gap-20">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i}>
                                            <div className="d-flex justify-content-between mb-8">
                                                <SkeletonBar width="40%" height={12} />
                                                <SkeletonBar width="25%" height={12} />
                                            </div>
                                            <SkeletonBar height={8} />
                                        </div>
                                    ))}
                                </div>
                            ) : analytics?.categories?.length ? (
                                <div className="d-flex flex-column gap-20">
                                    {analytics.categories.slice(0, 6).map((cat, i) => {
                                        const pct = (cat.count / maxCategoryCount) * 100;
                                        const color = categoryColors[i % categoryColors.length];
                                        return (
                                            <div key={i}>
                                                <div
                                                    className="d-flex justify-content-between align-items-center mb-8"
                                                >
                                                    <span className="fw-medium" style={{ fontSize: 13 }}>
                                                        {cat.name}
                                                    </span>
                                                    <span style={{ fontSize: 12, color: "#bbb" }}>
                                                        {cat.count}x · ${cat.savings.toFixed(0)} saved
                                                    </span>
                                                </div>
                                                <div
                                                    style={{
                                                        background: "#f5f5f5",
                                                        height: 8,
                                                        borderRadius: 99,
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: `${pct}%`,
                                                            height: "100%",
                                                            background: color,
                                                            borderRadius: 99,
                                                            transition: "width 0.8s ease",
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p style={{ fontSize: 14, color: "#bbb" }}>
                                    No category data yet.
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Favorite Businesses + Achievements ── */}
                <div className="row gy-4 mb-32">
                    {/* Top Businesses */}
                    <div className="col-lg-5">
                        <div className="p-32 rounded-16 h-100" style={cardStyle}>
                            <h6 className="fw-bold mb-4" style={{ fontSize: 16 }}>Favorite Businesses</h6>
                            <p style={{ fontSize: 13, color: "#aaa", marginBottom: 24 }}>
                                Your most-visited spots
                            </p>
                            {analyticsLoading ? (
                                <div className="d-flex flex-column gap-12">
                                    {[1, 2, 3].map((i) => (
                                        <div
                                            key={i}
                                            className="rounded-12"
                                            style={{
                                                height: 64,
                                                background: "#f5f5f5",
                                                animation: "pulse 1.5s ease-in-out infinite",
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : analytics?.top_businesses?.length ? (
                                <div className="d-flex flex-column gap-10">
                                    {analytics.top_businesses.map((biz, i) => (
                                        <div
                                            key={i}
                                            className="d-flex align-items-center gap-14 p-14 rounded-12"
                                            style={{ background: "#f8f9fb", border: "1px solid #f0f0f0" }}
                                        >
                                            <div
                                                className="d-flex align-items-center justify-content-center rounded-circle fw-bold flex-shrink-0"
                                                style={{
                                                    width: 40,
                                                    height: 40,
                                                    background:
                                                        i === 0
                                                            ? "linear-gradient(135deg,#FFD700,#FFA500)"
                                                            : i === 1
                                                            ? "linear-gradient(135deg,#C0C0C0,#A9A9A9)"
                                                            : i === 2
                                                            ? "linear-gradient(135deg,#CD7F32,#A0522D)"
                                                            : "#eef4ff",
                                                    color: i < 3 ? "#fff" : "#2575fc",
                                                    fontSize: 16,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}
                                            </div>
                                            <div className="flex-1" style={{ minWidth: 0 }}>
                                                <p
                                                    className="mb-0 fw-semibold"
                                                    style={{
                                                        fontSize: 14,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}
                                                >
                                                    {biz.name}
                                                </p>
                                                <p className="mb-0" style={{ fontSize: 12, color: "#bbb" }}>
                                                    {biz.city || "—"} &middot; {biz.count} visit
                                                    {biz.count !== 1 ? "s" : ""}
                                                </p>
                                            </div>
                                            <span
                                                className="fw-bold flex-shrink-0"
                                                style={{ fontSize: 14, color: "#10b981" }}
                                            >
                                                ${biz.savings.toFixed(0)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p style={{ fontSize: 14, color: "#bbb" }}>
                                    No business data yet.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Achievements */}
                    <div className="col-lg-7">
                        <div className="p-32 rounded-16 h-100" style={cardStyle}>
                            <h6 className="fw-bold mb-4" style={{ fontSize: 16 }}>Achievements</h6>
                            <p style={{ fontSize: 13, color: "#aaa", marginBottom: 24 }}>
                                {badges.length > 0
                                    ? `You've earned ${badges.length} badge${badges.length !== 1 ? "s" : ""} — keep going!`
                                    : "Redeem coupons to start earning badges"}
                            </p>
                            {analyticsLoading ? (
                                <div className="d-flex flex-wrap gap-12">
                                    {[1, 2, 3, 4, 5, 6].map((i) => (
                                        <div
                                            key={i}
                                            className="rounded-16"
                                            style={{
                                                width: 96,
                                                height: 96,
                                                background: "#f5f5f5",
                                                animation: "pulse 1.5s ease-in-out infinite",
                                                animationDelay: `${i * 0.1}s`,
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="d-flex flex-wrap gap-12">
                                    {badges.map((badge, i) => (
                                        <div
                                            key={i}
                                            className="d-flex flex-column align-items-center text-center p-14 rounded-16"
                                            style={{
                                                background: `${badge.color}10`,
                                                border: `1px solid ${badge.color}28`,
                                                width: 96,
                                                cursor: "default",
                                                transition: "transform 0.15s",
                                            }}
                                            title={badge.desc}
                                            onMouseEnter={(e) =>
                                                (e.currentTarget.style.transform = "translateY(-2px)")
                                            }
                                            onMouseLeave={(e) =>
                                                (e.currentTarget.style.transform = "translateY(0)")
                                            }
                                        >
                                            <div
                                                className="d-flex align-items-center justify-content-center rounded-circle mb-8"
                                                style={{
                                                    width: 40,
                                                    height: 40,
                                                    background: `${badge.color}1e`,
                                                }}
                                            >
                                                <i
                                                    className={`ph ${badge.icon}`}
                                                    style={{ fontSize: 20, color: badge.color }}
                                                />
                                            </div>
                                            <span
                                                className="fw-semibold"
                                                style={{ fontSize: 11, lineHeight: 1.35, color: "#333" }}
                                            >
                                                {badge.label}
                                            </span>
                                        </div>
                                    ))}
                                    {badges.length === 0 &&
                                        LOCKED_BADGE_LABELS.map((label, i) => (
                                            <div
                                                key={i}
                                                className="d-flex flex-column align-items-center text-center p-14 rounded-16"
                                                style={{
                                                    background: "#f8f9fb",
                                                    border: "1px solid #f0f0f0",
                                                    width: 96,
                                                    opacity: 0.55,
                                                }}
                                            >
                                                <div
                                                    className="d-flex align-items-center justify-content-center rounded-circle mb-8"
                                                    style={{ width: 40, height: 40, background: "#f0f0f0" }}
                                                >
                                                    <i
                                                        className="ph ph-lock"
                                                        style={{ fontSize: 18, color: "#ccc" }}
                                                    />
                                                </div>
                                                <span
                                                    className="fw-semibold"
                                                    style={{ fontSize: 11, color: "#bbb" }}
                                                >
                                                    {label}
                                                </span>
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Cities Explored ── */}
                {!analyticsLoading && analytics?.cities?.length > 0 && (
                    <div className="p-32 rounded-16 mb-32" style={cardStyle}>
                        <div className="d-flex align-items-center gap-12 mb-20">
                            <i className="ph ph-map-pin" style={{ fontSize: 20, color: "#2575fc" }} />
                            <h6 className="fw-bold mb-0" style={{ fontSize: 16 }}>
                                Cities Explored
                            </h6>
                            <span
                                className="px-10 py-4 rounded-pill fw-semibold"
                                style={{ background: "#eef4ff", color: "#2575fc", fontSize: 12 }}
                            >
                                {analytics.cities.length}{" "}
                                {analytics.cities.length === 1 ? "city" : "cities"}
                            </span>
                        </div>
                        <div className="d-flex flex-wrap gap-8">
                            {analytics.cities.map((city, i) => (
                                <span
                                    key={i}
                                    className="d-inline-flex align-items-center gap-6 px-16 py-8 rounded-pill fw-medium"
                                    style={{
                                        background: "#f8f9fb",
                                        border: "1px solid #e8e8e8",
                                        fontSize: 13,
                                        color: "#444",
                                    }}
                                >
                                    <i className="ph ph-map-pin" style={{ fontSize: 13, color: "#2575fc" }} />
                                    {city}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Recent Redemptions Table ── */}
                <div className="p-32 rounded-16" style={cardStyle}>
                    <div
                        className="d-flex align-items-center justify-content-between flex-wrap gap-12 mb-24"
                    >
                        <div>
                            <h6 className="fw-bold mb-4" style={{ fontSize: 16 }}>
                                Recent Redemptions
                            </h6>
                            <p className="mb-0" style={{ fontSize: 13, color: "#aaa" }}>
                                {redemptions.length > 0
                                    ? `${redemptions.length} total redemption${redemptions.length !== 1 ? "s" : ""}`
                                    : ""}
                            </p>
                        </div>
                        {redemptions.length > 0 && (
                            <span
                                className="px-16 py-8 rounded-pill fw-semibold"
                                style={{ background: "#ecfdf5", color: "#10b981", fontSize: 13 }}
                            >
                                $
                                {redemptions
                                    .reduce((s, r) => s + (parseFloat(r.dollar_savings_amount) || 0), 0)
                                    .toFixed(2)}{" "}
                                total saved
                            </span>
                        )}
                    </div>

                    {redemptionsLoading && (
                        <div className="d-flex flex-column gap-12">
                            {[1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className="rounded-8"
                                    style={{
                                        height: 52,
                                        background: "#f5f5f5",
                                        animation: "pulse 1.5s ease-in-out infinite",
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    {!redemptionsLoading && redemptions.length === 0 && (
                        <div className="text-center py-48">
                            <i className="ph ph-ticket" style={{ fontSize: 48, color: "#e0e0e0" }} />
                            <p className="mt-16 mb-0" style={{ color: "#bbb", fontSize: 14 }}>
                                You haven't redeemed any coupons yet.
                            </p>
                            <Link
                                to="/shop"
                                className="d-inline-block px-24 py-10 rounded-pill fw-semibold mt-16"
                                style={{
                                    background: "#eef4ff",
                                    color: "#2575fc",
                                    textDecoration: "none",
                                    fontSize: 13,
                                }}
                            >
                                Browse Coupons
                            </Link>
                        </div>
                    )}

                    {!redemptionsLoading && redemptions.length > 0 && (
                        <div className="table-responsive">
                            <table
                                className="table table-borderless mb-0"
                                style={{ fontSize: 14 }}
                            >
                                <thead>
                                    <tr style={{ borderBottom: "2px solid #f5f5f5" }}>
                                        {["Coupon", "Category", "Business", "Savings", "Date"].map((h) => (
                                            <th
                                                key={h}
                                                className="pb-14 fw-medium"
                                                style={{
                                                    fontSize: 11,
                                                    textTransform: "uppercase",
                                                    letterSpacing: 0.6,
                                                    color: "#bbb",
                                                    paddingLeft: h === "Coupon" ? 0 : undefined,
                                                }}
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {redemptions.map((r) => (
                                        <tr
                                            key={r.id}
                                            style={{ borderBottom: "1px solid #f8f8f8" }}
                                        >
                                            <td className="py-16 fw-semibold" style={{ paddingLeft: 0 }}>
                                                <Link
                                                    to={`/coupon/${r.coupon_id}`}
                                                    style={{ color: "#3A6BA8", textDecoration: "none" }}
                                                >
                                                    {r.coupon_title}
                                                </Link>
                                            </td>
                                            <td className="py-16">
                                                <span
                                                    className="px-10 py-4 rounded-pill fw-medium"
                                                    style={{
                                                        background: "#f5f3ff",
                                                        color: "#8b5cf6",
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {r.discount_category || "—"}
                                                </span>
                                            </td>
                                            <td
                                                className="py-16"
                                                style={{ fontSize: 13, color: "#888" }}
                                            >
                                                {r.business_name || "—"}
                                            </td>
                                            <td className="py-16">
                                                <span className="fw-bold" style={{ color: "#10b981" }}>
                                                    ${parseFloat(r.dollar_savings_amount || 0).toFixed(2)}
                                                </span>
                                            </td>
                                            <td
                                                className="py-16"
                                                style={{ fontSize: 13, color: "#bbb" }}
                                            >
                                                {formatDate(r.redeemed_on)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </section>
    );
};

export default UserDashboard;
